import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useLocalization } from "../../hooks/useLocalization";
import { contractApi } from "../../services/contractApi";
import { noticeApi } from "../../services/noticeApi";
import { StatusBadge } from "../../components/StatusBadge";
import { SignaturePanel } from "../../components/SignaturePanel";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";

const EMPLOYEE_VISIBLE_NOTICE_STATUSES = ["ISSUED", "ACKNOWLEDGED"];

export default function MyContractPage() {
  const { user } = useAuth();
  const { t, language } = useLocalization();
  const [contract, setContract] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    if (user?.employee_id) {
      contractApi.getByEmployee(user.employee_id).then((r) => setContract(r.data)).catch(() => setContract(null));
      noticeApi.getCurrentByEmployee(user.employee_id).then((r) => setNotice(r.data)).catch(() => setNotice(null)).finally(() => setLoaded(true));
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleSign = async (typedName) => {
    await contractApi.signEmployee(contract.id, typedName);
    load();
  };

  const handleSignNotice = async (typedName) => {
    await noticeApi.sign(notice.id, typedName);
    load();
  };

  const visibleNotice = notice && EMPLOYEE_VISIBLE_NOTICE_STATUSES.includes(notice.status) ? notice : null;

  if (!loaded) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;
  if (!contract && !visibleNotice) return <EagleState variant="empty" title={t("eagle.noContractsTitle")} message={t("eagle.noContractsMessage")} testId="no-contract-message" />;

  return (
    <div data-testid="my-contract-page" className="space-y-6">
      <PageHelpBanner pageKey="my-contract" title="How to use this page">
        <p>Read your employment contract and Article 36 notice carefully, then type your name in the signature box when it's ready for you to sign - once signed, it's permanently on file here.</p>
      </PageHelpBanner>
      <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("nav.myContract")}</h2>

      {contract && (
        <div data-testid="my-contract-section" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">{t("contracts.title")}</p>
            <StatusBadge status={contract.status} />
          </div>
          {contract.status === "FULLY_EXECUTED" ? (
            <EagleState variant="success" title={t("eagle.contractExecutedTitle")} message={t("eagle.contractExecutedMessage")} testId="my-contract-executed" />
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-5 max-h-96 overflow-y-auto whitespace-pre-wrap text-sm text-gray-700" data-testid="contract-content">
              {language === "ja" ? contract.content_ja : contract.content_en}
            </div>
          )}
          {contract.status === "AWAITING_EMPLOYEE_SIGNATURE" && (
            <SignaturePanel label={t("contracts.signAsEmployee")} onSign={handleSign} />
          )}
          {contract.employee_signature && (
            <p className="text-xs text-gray-500" data-testid="employee-signature-info">
              Signed by {contract.employee_signature.typed_name} on {contract.employee_signature.signed_at}
            </p>
          )}
        </div>
      )}

      {visibleNotice && (
        <div data-testid="my-notice-section" className="space-y-3 border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">{t("notice.title")}</p>
            <StatusBadge status={visibleNotice.status} />
          </div>
          {visibleNotice.status === "ACKNOWLEDGED" ? (
            <EagleState variant="success" title={t("notice.signSuccess")} message={t("notice.subtitle")} testId="my-notice-acknowledged" />
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-5 text-sm text-gray-700 space-y-1" data-testid="notice-summary-content">
              <p><span className="font-semibold">{t("notice.workplace")}:</span> {visibleNotice.workplace}</p>
              <p><span className="font-semibold">{t("notice.jobDescription")}:</span> {visibleNotice.job_description}</p>
              <p><span className="font-semibold">{t("notice.periodStart")}:</span> {visibleNotice.employment_period_start}</p>
            </div>
          )}
          {visibleNotice.status === "ISSUED" && (
            <SignaturePanel label={t("notice.sign")} onSign={handleSignNotice} />
          )}
          {visibleNotice.employee_signature && (
            <p className="text-xs text-gray-500" data-testid="notice-signature-info">
              {t("notice.signedBy")}: {visibleNotice.employee_signature.typed_name} · {visibleNotice.employee_signature.signed_at}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
